import React, { useState, useEffect, useRef, useContext } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import api from '../../../api/axios';

function ChatComponent({ consultationId, currentUserId, wsBaseUrl }) {
  const { token } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [isTyping, setIsTyping] = useState(false);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);

  // Custom CSS for scrollbar
  const scrollbarStyles = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: transparent;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #cbd5e0;
      border-radius: 3px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #a0aec0;
    }
  `;

  // Add styles to document head
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = scrollbarStyles;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Chat time restriction
  const currentHour = new Date().getHours();
  const isChatAllowed = currentHour >= 9 && currentHour < 22;

  // Load consultation messages
  useEffect(() => {
    if (!token || !consultationId) return;
    
    async function fetchMessages() {
      try {
        const res = await api.get(`/consultation/${consultationId}/messages/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.data && res.data.success && Array.isArray(res.data.messages)) {
          setMessages(res.data.messages);
        } else {
          setMessages([]);
        }
      } catch (err) {
        console.error('Error loading consultation history:', err);
        setMessages([]);
      }
    }
    
    fetchMessages();
  }, [consultationId, token]);

  // Enhanced WebSocket setup
  useEffect(() => {
    if (!token || !isChatAllowed || !wsBaseUrl) return;

    const wsUrl = `${wsBaseUrl.replace(/\/$/, '')}/ws/chat/${consultationId}/?token=${token}`;
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      console.log('Medical chat connected');
      setConnectionStatus('connected');
    };

    wsRef.current.onerror = (err) => {
      console.error('Chat connection error:', err);
      setConnectionStatus('error');
    };

    wsRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'message') {
          setMessages(prev => [...prev, {
            id: data.message_id || Date.now(),
            content: data.message,
            sender_name: data.sender_name,
            sender_id: data.sender_id,
            sender_type: data.sender_type,
            timestamp: data.timestamp || new Date().toISOString(),
            is_doctor: data.sender_type === 'doctor'
          }]);
        } else if (data.type === 'typing') {
          setIsTyping(data.is_typing && data.sender_id !== currentUserId);
        } else if (data.type === 'connection_status') {
          console.log(`Connected to consultation ${data.consultation_id}`);
          setConnectionStatus('connected');
        } else if (data.type === 'error') {
          console.error('Medical chat error:', data.message);
          setConnectionStatus('error');
        }
      } catch (err) {
        console.error('Error parsing chat message:', err);
      }
    };

    wsRef.current.onclose = (event) => {
      console.log('Medical chat disconnected');
      setConnectionStatus(event.wasClean ? 'disconnected' : 'error');
    };

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [consultationId, token, wsBaseUrl, isChatAllowed, currentUserId]);

  // Handle scroll detection
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
    setIsUserScrolledUp(!isAtBottom);
  };

  // Auto-scroll to bottom (only if user hasn't scrolled up)
  useEffect(() => {
    if (!isUserScrolledUp) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping, isUserScrolledUp]);

  // Scroll to bottom when new message from current user
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.sender_id === currentUserId) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        setIsUserScrolledUp(false);
      }
    }
  }, [messages, currentUserId]);

  // Dynamic textarea height adjustment (WhatsApp-like)
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '20px';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = Math.min(scrollHeight, 100) + 'px';
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [newMessage]);

  // Send typing indicator
  const handleTyping = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        is_typing: true
      }));

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'typing',
            is_typing: false
          }));
        }
      }, 1000);
    }
  };

  const sendMessage = () => {
    if (!token || !isChatAllowed) return;

    if (newMessage.trim() && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        message: newMessage.trim(),
      }));
      setNewMessage('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = '20px';
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    } else {
      handleTyping();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setIsUserScrolledUp(false);
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-600';
      case 'connecting': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Online';
      case 'connecting': return 'Connecting...';
      case 'error': return 'Connection failed';
      default: return 'Offline';
    }
  };

  if (!token) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg border">
        <div className="text-center p-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Authentication Required</h3>
          <p className="text-gray-600">Please log in to access medical chat services</p>
        </div>
      </div>
    );
  }

  if (!isChatAllowed) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg border">
        <div className="text-center p-6">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Medical Chat Hours</h3>
          <p className="text-gray-600 mb-2">Our medical chat service is available:</p>
          <p className="text-amber-700 font-medium">9:00 AM - 10:00 PM daily</p>
          <p className="text-sm text-gray-500 mt-2">For emergencies, please contact emergency services</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-h-[80vh] bg-white rounded-lg border shadow-sm">
      {/* Header with connection status - WhatsApp style */}
      <div className="flex items-center p-3 border-b bg-gray-50">
        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mr-3">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.712 4.33a9.865 9.865 0 01-1.587 6.24L6.34 19.353a.5.5 0 01-.707-.707L14.416 9.863a9.865 9.865 0 016.24-1.587c.194 0 .389-.006.583-.017l1.478-1.478a.5.5 0 01.353-.853h3.5a.5.5 0 01.5.5v3.5a.5.5 0 01-.853.353L24.74 8.874c-.011.194-.017.389-.017.583z" />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900">Medical Team</h3>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              connectionStatus === 'connected' ? 'bg-green-500' : 
              connectionStatus === 'connecting' ? 'bg-yellow-500' : 'bg-red-500'
            }`}></div>
            <span className={`text-xs ${getConnectionStatusColor()}`}>
              {getConnectionStatusText()}
            </span>
          </div>
        </div>
        <div className="text-xs text-gray-500">
          #{consultationId}
        </div>
      </div>

      {/* Messages area with fixed height and scroll */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto bg-gray-50 relative custom-scrollbar"
        onScroll={handleScroll}
        style={{ 
          minHeight: '200px',
          maxHeight: 'calc(100vh - 400px)', // Leave more space for input area
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23f3f4f6' fill-opacity='0.3'%3E%3Cpath d='M20 20c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8zm0-20c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8zm20 0c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8zm0 20c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8z'/%3E%3C/g%3E%3C/svg%3E")`,
          scrollBehavior: 'smooth',
        }}
      >
        <div className="p-4 space-y-3">
          {messages.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-gray-500 font-semibold text-lg">Start your consultation</p>
              <p className="text-gray-400 text-base mt-2">A healthcare professional will respond shortly</p>
            </div>
          ) : (
            messages.map((msg, index) => {
              const isCurrentUser = msg.sender_id === currentUserId;
              const showDate = index === 0 || 
                formatDate(messages[index - 1].timestamp) !== formatDate(msg.timestamp);

              return (
                <div key={msg.id}>
                  {showDate && (
                    <div className="flex justify-center my-6">
                      <div className="bg-white px-4 py-2 rounded-full text-sm text-gray-600 shadow-sm font-medium">
                        {formatDate(msg.timestamp)}
                      </div>
                    </div>
                  )}
                  
                  <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-3`}>
                    <div 
                      className={`max-w-sm lg:max-w-lg px-4 py-3 rounded-2xl shadow-sm relative ${
                        isCurrentUser
                          ? 'bg-blue-500 text-white rounded-br-md'
                          : msg.is_doctor
                          ? 'bg-white text-gray-900 border border-green-200 rounded-bl-md'
                          : 'bg-white text-gray-900 rounded-bl-md'
                      }`}
                      style={{
                        wordWrap: 'break-word',
                        wordBreak: 'break-word',
                        minWidth: '120px'
                      }}
                    >
                      {!isCurrentUser && (
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`text-sm font-semibold ${
                            msg.is_doctor ? 'text-green-600' : 'text-gray-600'
                          }`}>
                            {msg.sender_name || (msg.is_doctor ? 'Dr.' : 'Patient')}
                          </span>
                          {msg.is_doctor && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              MD
                            </span>
                          )}
                        </div>
                      )}
                      
                      <p className="text-base leading-relaxed whitespace-pre-wrap">
                        {msg.message || msg.content}
                      </p>
                      
                      <div className={`text-xs mt-2 text-right ${
                        isCurrentUser ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {formatTime(msg.timestamp)}
                        {isCurrentUser && (
                          <svg className="w-4 h-4 inline ml-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          
          {isTyping && (
            <div className="flex justify-start mb-3">
              <div className="bg-white rounded-2xl px-4 py-3 shadow-sm rounded-bl-md">
                <div className="flex space-x-1 items-center">
                  <div className="flex space-x-1">
                    <div className="w-3 h-3 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-3 h-3 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-3 h-3 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-sm text-gray-500 ml-2">Typing...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Scroll to bottom button */}
        {isUserScrolledUp && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-4 right-4 w-12 h-12 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600 transition-all duration-200 flex items-center justify-center group"
            title="Scroll to bottom"
          >
            <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </button>
        )}
      </div>

      {/* Input area - WhatsApp style - Always visible at bottom */}
      <div className="bg-white p-4 border-t flex-shrink-0">
        <div className="flex items-end space-x-3">
          <div className="flex-1 bg-gray-100 rounded-full px-5 py-2 min-h-[48px] flex items-center">
            <textarea
              ref={textareaRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              className="w-full bg-transparent resize-none border-none outline-none text-base placeholder-gray-500"
              style={{
                minHeight: '24px',
                maxHeight: '120px',
                lineHeight: '1.5'
              }}
              rows={1}
            />
          </div>
          
          <button
            onClick={sendMessage}
            disabled={!newMessage.trim() || connectionStatus !== 'connected'}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${
              newMessage.trim() && connectionStatus === 'connected'
                ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatComponent;